import { PayloadAction } from '@reduxjs/toolkit';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { isFormDirty } from '../selectors/quote-builder-dirty';
import { stringifyQuote } from './stringify-quote';

/**
 * Wraps a reducer to update `lastSaveDateTime` and `currentServerData` after
 * the reducer runs, but ONLY if the form was clean before the mutation.
 *
 * ## Why this approach?
 *
 * Many actions in the Quote Builder persist only a subset of state (e.g., adding
 * a quote line, deleting an attachment). These mutations don't save other pending
 * changes to quote-level fields. If we always updated `currentServerData` after
 * these actions, we'd incorrectly mark the form as "saved" even when other unsaved
 * changes exist.
 *
 * This approach doesn't work well for async actions that optimistically update
 * state because we only have the ability to wrap individual reducers for
 * fulfilled/rejected states, not the entire async thunk, so we can't check for
 * dirty state prior to the optimistic mutations.
 */
export function withSaveStateUpdate<TPayload>(
  reducer: (
    state: WritableQuoteBuilderStateType,
    action: PayloadAction<TPayload>,
  ) => void,
) {
  return (
    state: WritableQuoteBuilderStateType,
    action: PayloadAction<TPayload>,
  ) => {
    const wasFormDirty = isFormDirty(state);

    reducer(state, action);

    if (!wasFormDirty) {
      state.lastSaveDateTime = new Date();
      state.currentServerData = stringifyQuote(state);
    }
  };
}
